import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShoppingBag, TrendingUp, Users, Calendar, CheckCircle, Award } from 'lucide-react';
import LandingNav from '../../components/Landing-Nav';
import FooterLanding from '../../components/FooterLanding';

const RetailIndustry = () => {
  const features = [
    { icon: Calendar, title: 'Peak Hour Optimization', description: 'Automatically schedule more staff during busy shopping hours' },
    { icon: Users, title: 'Multi-Location Management', description: 'Manage schedules across all your retail locations' },
    { icon: ShoppingBag, title: 'POS Integration', description: 'Connect scheduling with sales data for smart staffing' },
    { icon: TrendingUp, title: 'Seasonal Planning', description: 'Scale your workforce for holidays and sales events' }
  ];

  const benefits = [
    '42% improvement in coverage',
    '35% reduction in Employee waste',
    '91% staff engagement',
    'Automated shift swapping',
    'Mobile app for staff',
    'Real-time sales tracking'
  ];

  const stats = [
    { value: '600+', label: 'Retail Stores' },
    { value: '42%', label: 'Better Coverage' },
    { value: '91%', label: 'Staff Engagement' },
    { value: '35%', label: 'Employee Waste Reduction' }
  ];

  return (
    <>
      <LandingNav />
      
      <section className="pt-24 pb-16 bg-gradient-to-br from-pink-600 via-rose-600 to-red-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
              <div className="flex items-center space-x-2 mb-4">
                <ShoppingBag className="w-8 h-8" />
                <span className="text-pink-200 font-semibold">Retail & Multi-Location</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Optimize Retail Staffing for Maximum Customer Satisfaction
              </h1>
              <p className="text-xl text-pink-100 mb-8">
                Match your workforce to customer demand with intelligent scheduling that adapts to sales patterns and foot traffic.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup" className="px-8 py-4 bg-white text-pink-600 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all text-center">
                  Schedule a Demo
                </Link>
                <Link to="/pricing" className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all text-center">
                  View Pricing
                </Link>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <img src="/Schedule Page.png" alt="Retail Dashboard" className="rounded-2xl shadow-2xl border-4 border-white/20" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-pink-600 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Built for Retail Excellence</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">From boutiques to big box stores, manage your workforce efficiently</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all">
                  <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-pink-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }}>
              <img src="/Admin Dashboard.png" alt="Analytics Dashboard" className="rounded-2xl shadow-2xl" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }}>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Real Results for Retail Operations</h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                    <span className="text-lg text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-pink-50 to-rose-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="w-16 h-16 text-pink-600 mx-auto mb-6" />
          <blockquote className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            "Shiftry helped us eliminate Employee waste during slow periods while ensuring we're fully staffed for peak hours."
          </blockquote>
          <p className="text-lg text-gray-600">- Amanda Williams, Regional Manager, StyleHub Retail</p>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-r from-pink-600 to-red-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Ready to Optimize Your Retail Operations?</h2>
          <p className="text-xl text-pink-100 mb-8">Join 600+ retail stores delivering excellent customer experiences</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="px-8 py-4 bg-white text-pink-600 rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
              Schedule Your Free Demo
            </Link>
            <Link to="/pricing" className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all">
              View Pricing Plans
            </Link>
          </div>
        </div>
      </section>

      <FooterLanding />
    </>
  );
};

export default RetailIndustry;
